import { FormEvent, useEffect, useMemo, useState } from 'react'
import AdminMfaQr from './AdminMfaQr'

type LoginStep = 'password' | 'mfa' | 'enroll' | 'recovery-codes'

type ApiError = { code?: string; message?: string }

type EnrollmentDetails = {
  secret: string
  otpauthUri: string
  digits: number
  periodSeconds: number
}

type EnrollmentSuccess = {
  authenticated: true
  recoveryCodes: string[]
  csrfToken: string
}

async function readJson<T>(response: Response): Promise<T & ApiError> {
  try {
    return await response.json() as T & ApiError
  } catch {
    return { message: 'Something went wrong. Please try again.' } as T & ApiError
  }
}

async function postJson<T>(path: string, body: Record<string, unknown>) {
  const response = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return { response, data: await readJson<T>(response) }
}

function BrandPanel() {
  return (
    <section className="admin-login-brand" aria-hidden="true">
      <div className="admin-login-brand__noise" />
      <span className="admin-login-brand__badge">PRIVATE HQ</span>
      <div className="admin-login-brand__content">
        <img
          className="admin-login-brand__logo"
          src="/assets/brand/sg-horizontal-logo-approved.png"
          alt=""
        />
        <p className="admin-login-brand__eyebrow">BOSS ZONE</p>
        <h1>Run the chaos.<br />Keep the magic.</h1>
        <p className="admin-login-brand__copy">
          Orders, menu drops and the operational stuff live behind this door.
        </p>
      </div>
      <img
        className="admin-login-brand__mascot"
        src="/assets/brand/sg-mascot-head-approved.png"
        alt=""
      />
      <span className="admin-login-brand__scribble admin-login-brand__scribble--one">✦</span>
      <span className="admin-login-brand__scribble admin-login-brand__scribble--two">✦</span>
    </section>
  )
}

function PasswordField({
  value,
  onChange,
  disabled,
}: {
  value: string
  onChange: (value: string) => void
  disabled: boolean
}) {
  const [visible, setVisible] = useState(false)
  return (
    <div className="admin-login-field">
      <label htmlFor="admin-password">Password</label>
      <div className="admin-password-wrap">
        <input
          id="admin-password"
          autoComplete="current-password"
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled}
          required
          maxLength={128}
        />
        <button
          className="admin-password-toggle"
          type="button"
          aria-label={visible ? 'Hide password' : 'Show password'}
          aria-pressed={visible}
          onClick={() => setVisible((current) => !current)}
          disabled={disabled}
        >
          {visible ? 'Hide' : 'Show'}
        </button>
      </div>
    </div>
  )
}

function SecurityNote() {
  return (
    <div className="admin-security-note">
      <span className="admin-security-note__icon" aria-hidden="true">◆</span>
      <span>Owner access is protected by password, authenticator MFA and secure sessions.</span>
    </div>
  )
}

export default function AdminLoginPage() {
  const [step, setStep] = useState<LoginStep>('password')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [useRecovery, setUseRecovery] = useState(false)
  const [enrollment, setEnrollment] = useState<EnrollmentDetails | null>(null)
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([])
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState(false)

  const nextPath = useMemo(() => {
    const requested = new URLSearchParams(window.location.search).get('next')
    return requested?.startsWith('/admin') && !requested.startsWith('//') ? requested : '/admin'
  }, [])

  useEffect(() => {
    void fetch('/api/admin/session', { credentials: 'same-origin' }).then((response) => {
      if (response.ok) window.location.replace(nextPath)
    }).catch(() => undefined)
  }, [nextPath])

  async function loadEnrollment() {
    const response = await fetch('/api/admin/auth/mfa/enrollment', { credentials: 'same-origin' })
    const data = await readJson<EnrollmentDetails>(response)
    if (!response.ok) throw new Error(data.message || 'MFA setup could not be started.')
    setEnrollment(data)
  }

  async function submitPassword(event: FormEvent) {
    event.preventDefault()
    if (busy) return
    setBusy(true)
    setError('')
    try {
      const { response, data } = await postJson<{ next?: string }>('/api/admin/auth/password', { email, password })
      if (!response.ok) {
        setError(data.message || 'Email or password was not accepted.')
        return
      }
      setPassword('')
      setCode('')
      if (data.next === 'mfa_enrollment_required') {
        setStep('enroll')
        await loadEnrollment()
      } else {
        setStep('mfa')
      }
    } catch {
      setError('Admin security is temporarily unavailable. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  async function submitMfa(event: FormEvent) {
    event.preventDefault()
    if (busy) return
    setBusy(true)
    setError('')
    try {
      const { response, data } = await postJson<{ authenticated?: boolean }>('/api/admin/auth/mfa/verify', { code })
      if (!response.ok || !data.authenticated) {
        setError(data.message || 'That verification code was not accepted.')
        return
      }
      window.location.replace(nextPath)
    } catch {
      setError('Verification could not be completed. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  async function submitEnrollment(event: FormEvent) {
    event.preventDefault()
    if (busy) return
    setBusy(true)
    setError('')
    try {
      const { response, data } = await postJson<EnrollmentSuccess>('/api/admin/auth/mfa/enroll', { code })
      if (!response.ok || !data.authenticated || !Array.isArray(data.recoveryCodes)) {
        setError(data.message || 'That authenticator code was not accepted.')
        return
      }
      setRecoveryCodes(data.recoveryCodes)
      setStep('recovery-codes')
      setCode('')
    } catch {
      setError('MFA setup could not be completed. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  function restart() {
    setStep('password')
    setPassword('')
    setCode('')
    setUseRecovery(false)
    setEnrollment(null)
    setError('')
  }

  async function copyRecoveryCodes() {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join('\n'))
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1800)
    } catch {
      setCopied(false)
    }
  }

  return (
    <main className="admin-login-page">
      <BrandPanel />
      <section className="admin-login-workspace">
        <div className="admin-login-mobile-brand">
          <img src="/assets/brand/sg-horizontal-logo-approved.png" alt="Sugar Gremlin" />
          <span>BOSS ZONE</span>
        </div>

        <div className="admin-login-card">
          {step === 'password' && (
            <>
              <div className="admin-login-heading">
                <span className="admin-login-kicker">OWNER ACCESS</span>
                <h2>Welcome back, boss.</h2>
                <p>Sign in to Sugar Gremlin Boss Zone.</p>
              </div>
              <form onSubmit={submitPassword} noValidate>
                <div className="admin-login-field">
                  <label htmlFor="admin-email">Email address</label>
                  <input
                    id="admin-email"
                    type="email"
                    autoComplete="username"
                    inputMode="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    disabled={busy}
                    required
                    maxLength={254}
                  />
                </div>
                <PasswordField value={password} onChange={setPassword} disabled={busy} />
                {error && <div className="admin-login-error" role="alert">{error}</div>}
                <button className="admin-login-primary" type="submit" disabled={busy || !email || !password}>
                  {busy ? 'Checking…' : 'Continue'}
                </button>
              </form>
              <SecurityNote />
            </>
          )}

          {step === 'mfa' && (
            <>
              <button className="admin-login-back" type="button" onClick={restart}>← Back to sign in</button>
              <div className="admin-login-heading">
                <span className="admin-login-kicker">SECOND STEP</span>
                <h2>{useRecovery ? 'Use a recovery code.' : 'Prove it’s you.'}</h2>
                <p>
                  {useRecovery
                    ? 'Enter one unused Boss Zone recovery code.'
                    : 'Enter the 6-digit code from your authenticator app.'}
                </p>
              </div>
              <form onSubmit={submitMfa} noValidate>
                <div className="admin-login-field">
                  <label htmlFor="admin-mfa-code">{useRecovery ? 'Recovery code' : 'Authenticator code'}</label>
                  <input
                    id="admin-mfa-code"
                    className={useRecovery ? '' : 'admin-login-otp'}
                    autoComplete="one-time-code"
                    inputMode={useRecovery ? 'text' : 'numeric'}
                    value={code}
                    onChange={(event) => setCode(useRecovery ? event.target.value.toUpperCase() : event.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder={useRecovery ? 'SG-XXXX-XXXX-XXXX-XXXX' : '000000'}
                    disabled={busy}
                    required
                    maxLength={useRecovery ? 32 : 6}
                    autoFocus
                  />
                </div>
                {error && <div className="admin-login-error" role="alert">{error}</div>}
                <button className="admin-login-primary" type="submit" disabled={busy || !code}>
                  {busy ? 'Verifying…' : 'Verify & enter'}
                </button>
              </form>
              <button
                className="admin-login-text-action"
                type="button"
                onClick={() => { setUseRecovery((current) => !current); setCode(''); setError('') }}
              >
                {useRecovery ? 'Use authenticator app instead' : 'Use a recovery code'}
              </button>
            </>
          )}

          {step === 'enroll' && (
            <>
              <button className="admin-login-back" type="button" onClick={restart}>← Back to sign in</button>
              <div className="admin-login-heading">
                <span className="admin-login-kicker">SECURE YOUR BOSS ZONE</span>
                <h2>Set up authenticator MFA.</h2>
                <p>Scan the QR code with your authenticator app, then enter the current 6-digit code.</p>
              </div>
              {enrollment ? (
                <>
                  <AdminMfaQr otpauthUri={enrollment.otpauthUri} />
                  <div className="admin-enrollment-key admin-enrollment-key--with-qr">
                    <span>Manual setup key</span>
                    <strong>{enrollment.secret.match(/.{1,4}/g)?.join(' ')}</strong>
                    <small>Issuer: Sugar Gremlin Boss Zone · 30-second codes</small>
                  </div>
                  <form onSubmit={submitEnrollment} noValidate>
                    <div className="admin-login-field">
                      <label htmlFor="admin-enroll-code">Authenticator code</label>
                      <input
                        id="admin-enroll-code"
                        className="admin-login-otp"
                        autoComplete="one-time-code"
                        inputMode="numeric"
                        value={code}
                        onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000"
                        disabled={busy}
                        required
                        maxLength={6}
                        autoFocus
                      />
                    </div>
                    {error && <div className="admin-login-error" role="alert">{error}</div>}
                    <button className="admin-login-primary" type="submit" disabled={busy || code.length !== 6}>
                      {busy ? 'Securing…' : 'Finish MFA setup'}
                    </button>
                  </form>
                </>
              ) : (
                <div className="admin-login-loading" role="status">Preparing secure MFA setup…</div>
              )}
            </>
          )}

          {step === 'recovery-codes' && (
            <>
              <div className="admin-login-heading">
                <span className="admin-login-kicker">ONE-TIME RECOVERY</span>
                <h2>Save these somewhere safe.</h2>
                <p>Each recovery code works once. They will not be shown again.</p>
              </div>
              <div className="admin-recovery-grid" aria-label="Recovery codes">
                {recoveryCodes.map((recoveryCode) => <code key={recoveryCode}>{recoveryCode}</code>)}
              </div>
              <button className="admin-login-secondary" type="button" onClick={() => void copyRecoveryCodes()}>
                {copied ? 'Copied' : 'Copy recovery codes'}
              </button>
              <button className="admin-login-primary" type="button" onClick={() => window.location.replace(nextPath)}>
                I’ve saved them · Enter Boss Zone
              </button>
              <p className="admin-recovery-warning">If you lose both your authenticator and these codes, owner recovery requires Development verification.</p>
            </>
          )}
        </div>

        <p className="admin-login-footer">Sugar Gremlin Boss Zone · Authorised access only</p>
      </section>
    </main>
  )
}
